import { SemanticModel, TreeNode, MetricDetail, DimensionDetail, InheritedModel, MetricGroup, DateRangePreset, MetricConfig } from './types';

// Granularity suffixes that the backend accepts on dimension strings.
const GRANULARITY_SUFFIXES = ['__year', '__quarter', '__month', '__week', '__day'];

// Time periods that expand into 3 output aliases (current/prior/change).
// Must match core/query_generator.py:_build_comparison_conditions().
const TIME_PERIOD_EXPANSION: Record<string, { current: string; prior: string; change: string }> = {
  MoM: { current: 'current_month', prior: 'prior_month', change: 'mom_pct_change' },
  QoQ: { current: 'current_quarter', prior: 'prior_quarter', change: 'qoq_pct_change' },
  YoY: { current: 'current_year', prior: 'prior_year', change: 'yoy_pct_change' },
};

/**
 * Primary sort column for a metric instance — the output alias the user
 * most commonly wants to order by. For time-period comparisons (MoM/QoQ/YoY)
 * this is the `_current_*` alias; for regular metrics and other time periods
 * it is the base alias (custom alias or metric name).
 */
export function getMetricPrimarySortColumn(metricName: string, config?: MetricConfig): string {
  const base = config?.alias || metricName;
  const tp = config?.timePeriod;
  if (tp && TIME_PERIOD_EXPANSION[tp]) {
    return `${base}_${TIME_PERIOD_EXPANSION[tp].current}`;
  }
  return base;
}

/**
 * Output alias for a selected dimension string (handles `__<gran>` suffix).
 * Must match the backend's _normalize/SELECT alias emission.
 */
export function getDimensionOutputAlias(dim: string): string {
  for (const suffix of GRANULARITY_SUFFIXES) {
    if (dim.endsWith(suffix) && dim.length > suffix.length) {
      // e.g. "order_date__month" → "order_date_month"
      return dim.slice(0, -suffix.length) + '_' + suffix.slice(2);
    }
  }
  return dim;
}

/**
 * Recursively filters tree nodes by a search term, matching against
 * label, metadata.name, and metadata.description. Parent nodes are
 * preserved when any child matches.
 */
export function filterTreeNodes(nodes: TreeNode[], term: string): TreeNode[] {
  if (!term) return nodes;
  const lower = term.toLowerCase();

  return nodes.reduce<TreeNode[]>((acc, node) => {
    const labelMatch = node.label.toLowerCase().includes(lower);
    const nameMatch = node.metadata?.name
      ? node.metadata.name.toLowerCase().includes(lower)
      : false;
    const descMatch = node.metadata?.description
      ? node.metadata.description.toLowerCase().includes(lower)
      : false;

    if (node.children && node.children.length > 0) {
      const filteredChildren = filterTreeNodes(node.children, term);
      if (filteredChildren.length > 0 || labelMatch || nameMatch || descMatch) {
        acc.push({
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : node.children,
        });
      }
    } else if (labelMatch || nameMatch || descMatch) {
      acc.push(node);
    }

    return acc;
  }, []);
}

/**
 * Maps a metric type string to a dataType for icon display.
 */
function metricDataType(type?: string): TreeNode['dataType'] {
  switch (type?.toLowerCase()) {
    case 'currency': return 'currency';
    case 'percentage': return 'percentage';
    case 'integer': return 'integer';
    case 'number': return 'number';
    default: return 'number';
  }
}

/**
 * Maps a dimension type string to a dataType for icon display.
 */
function dimensionDataType(type?: string): TreeNode['dataType'] {
  switch (type?.toLowerCase()) {
    case 'date':
    case 'datetime':
    case 'timestamp':
      return 'date';
    case 'boolean': return 'boolean';
    case 'integer': return 'integer';
    case 'number': return 'number';
    case 'json':
    case 'jsonb':
    case 'variant':
      return 'json';
    case 'categorical':
    case 'string':
    case 'text':
      return 'categorical';
    default: return 'categorical';
  }
}

/**
 * Converts a snake_case column name to a Title Case display name.
 * E.g. "payment_method" → "Payment Method", "order_date" → "Order Date"
 */
export function toDisplayName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const DATE_GRANULARITIES: { label: string; granularity: TreeNode['granularity'] }[] = [
  { label: 'Year', granularity: 'year' },
  { label: 'Quarter', granularity: 'quarter' },
  { label: 'Month', granularity: 'month' },
  { label: 'Week', granularity: 'week' },
  { label: 'Day', granularity: 'day' },
];

/**
 * Converts a SemanticModel from the API into an array of TreeNode objects
 * suitable for rendering in a hierarchical tree view.
 *
 * Tree structure:
 *   Model (root)
 *     -> Own/core dimensions
 *     -> Own/core metric groups -> metrics
 *     -> Inherited Model A (folder)
 *       -> Inherited dimensions
 *       -> Inherited metric groups -> metrics
 *     -> Inherited Model B (folder)
 *       -> ...
 */
export function buildTreeFromModel(model: SemanticModel): TreeNode[] {
  // Build lookup maps from the model's top-level metrics and dimensions
  const metricDetailMap = new Map<string, MetricDetail>();
  (model.metrics || []).forEach((m) => metricDetailMap.set(m.name, m));

  const dimensionDetailMap = new Map<string, DimensionDetail>();
  (model.dimensions || []).forEach((d) => dimensionDetailMap.set(d.name, d));

  // Helper: build a single dimension TreeNode from a DimensionDetail
  const buildDimensionNodeFromDetail = (detail: DimensionDetail, modelKey?: string): TreeNode => {
    const dType = dimensionDataType(detail.type);
    const displayName = toDisplayName(detail.name);

    if (dType === 'date') {
      const children: TreeNode[] = DATE_GRANULARITIES.map((g) => ({
        id: `date_hierarchy:${detail.name}__${g.granularity}`,
        label: g.label,
        type: 'date_hierarchy' as const,
        dataType: 'date' as const,
        granularity: g.granularity,
        model: modelKey,
        metadata: {
          name: `${detail.name}__${g.granularity}`,
          baseDimension: detail.name,
          displayName: g.label,
        },
      }));

      return {
        id: `dimension:${detail.name}`,
        label: displayName,
        type: 'dimension' as const,
        dataType: dType,
        children,
        model: modelKey,
        metadata: {
          ...detail,
          name: detail.name,
          displayName,
        },
      };
    }

    return {
      id: `dimension:${detail.name}`,
      label: displayName,
      type: 'dimension' as const,
      dataType: dType,
      model: modelKey,
      metadata: {
        ...detail,
        name: detail.name,
        displayName,
      },
    };
  };

  // Helper: build metric group TreeNodes from a groups array and a metric detail map
  const buildGroupNodes = (
    groups: MetricGroup[],
    detailMap: Map<string, MetricDetail>,
    modelKey?: string,
  ): TreeNode[] =>
    groups.map((group) => {
      const metricChildren: TreeNode[] = (group.metrics || []).filter((m) => !detailMap.get(m.name)?.hidden).map((metricRef) => {
        const detail = detailMap.get(metricRef.name);
        const mType = metricRef.type || detail?.type;
        return {
          id: `metric:${metricRef.name}`,
          label: metricRef.display_name || detail?.display_name || metricRef.name,
          type: 'metric' as const,
          dataType: metricDataType(mType),
          model: modelKey,
          metadata: {
            name: metricRef.name,
            use_cases: metricRef.use_cases,
            ...(detail || {}),
          },
        };
      });

      return {
        id: `group:${group.name}`,
        label: group.display_name || group.name,
        type: 'group' as const,
        children: metricChildren,
        model: modelKey,
        metadata: {
          name: group.name,
          description: group.description,
        },
      };
    });

  // Helper: metrics are listed through their groups, so a metric no group names (a model published without groups,
  // e.g. every Discovery model) gets an "All Metrics" folder rather than vanishing from the browser
  const withUngroupedMetrics = (
    groups: MetricGroup[],
    metrics: MetricDetail[],
    modelKey?: string,
  ): MetricGroup[] => {
    const grouped = new Set(groups.flatMap((g) => (g.metrics || []).map((m) => m.name)));
    // #2138: a hidden metric is out of the browser; a saved selection restores it by name, never from this tree
    const ungrouped = metrics.filter((m) => !grouped.has(m.name) && !m.hidden);
    if (!ungrouped.length) return groups;
    return [...groups, {
      name: `__ungrouped__${modelKey || ''}`,
      display_name: groups.length ? 'Other Metrics' : 'All Metrics',
      description: 'Metrics not assigned to a group',
      metrics: ungrouped.map((m) => ({ name: m.name })),
    } as MetricGroup];
  };

  // Helper: group dimension nodes that share a hierarchy into folder nodes
  const groupHierarchyDims = (dimNodes: TreeNode[], dimDetails: DimensionDetail[]): TreeNode[] => {
    const detailByName = new Map<string, DimensionDetail>();
    dimDetails.forEach((d) => detailByName.set(d.name, d));

    const hierarchyGroups = new Map<string, { detail: DimensionDetail; node: TreeNode }[]>();
    const nonHierarchyNodes: TreeNode[] = [];

    for (const node of dimNodes) {
      const dimName = node.metadata?.name;
      const detail = dimName ? detailByName.get(dimName) : undefined;
      if (detail?.hierarchy) {
        const hName = detail.hierarchy.name;
        if (!hierarchyGroups.has(hName)) {
          hierarchyGroups.set(hName, []);
        }
        hierarchyGroups.get(hName)!.push({ detail, node });
      } else {
        nonHierarchyNodes.push(node);
      }
    }

    const hierarchyFolderNodes: TreeNode[] = [];
    hierarchyGroups.forEach((members, hName) => {
      // Sort by level descending (broadest first)
      members.sort((a: { detail: DimensionDetail; node: TreeNode }, b: { detail: DimensionDetail; node: TreeNode }) =>
        b.detail.hierarchy!.level - a.detail.hierarchy!.level,
      );
      const folderNode: TreeNode = {
        id: `hierarchy:${hName}`,
        label: toDisplayName(hName),
        type: 'hierarchy' as const,
        children: members.map((m: { detail: DimensionDetail; node: TreeNode }) => m.node),
        metadata: {
          name: hName,
          description: `${toDisplayName(hName)} drill path: ${members.map((m: { detail: DimensionDetail; node: TreeNode }) => toDisplayName(m.detail.name)).join(' \u2192 ')}`,
        },
      };
      hierarchyFolderNodes.push(folderNode);
    });

    return [...hierarchyFolderNodes, ...nonHierarchyNodes];
  };

  // Own dimensions (from model.dimensions — these are own + core-merged)
  const ownDimNodes = (model.dimensions || []).map((d) =>
    buildDimensionNodeFromDetail(d, model.model),
  );
  const groupedOwnDimNodes = groupHierarchyDims(ownDimNodes, model.dimensions || []);

  // Own metric groups (from model.groups — these are own + core-merged)
  const ownGroupNodes = buildGroupNodes(
    withUngroupedMetrics(model.groups || [], model.metrics || [], model.model),
    metricDetailMap,
    model.model,
  );

  // Recursive builder for inherited model folder nodes (supports nesting)
  const buildInheritedModelNodes = (inheritedModels: InheritedModel[]): TreeNode[] =>
    (inheritedModels || []).map((inherited) => {
      // Build detail maps for inherited model's metrics
      const inhMetricMap = new Map<string, MetricDetail>();
      (inherited.metrics || []).forEach((m) => inhMetricMap.set(m.name, m));

      // Inherited dimensions (grouped by hierarchy where applicable)
      const inhDimNodes = (inherited.dimensions || []).map((d) =>
        buildDimensionNodeFromDetail(d, inherited.model),
      );
      const groupedInhDimNodes = groupHierarchyDims(inhDimNodes, inherited.dimensions || []);

      // Inherited metric groups
      const inhGroupNodes = buildGroupNodes(
        withUngroupedMetrics(inherited.groups || [], inherited.metrics || [], inherited.model),
        inhMetricMap,
        inherited.model,
      );

      // Nested inherited models (recursive)
      const nestedNodes = inherited.inheritedModels
        ? buildInheritedModelNodes(inherited.inheritedModels)
        : [];

      return {
        id: `inherited_model:${inherited.role ? `${inherited.role}_${inherited.model}` : inherited.model}`,
        label: inherited.display_name || inherited.model,
        type: 'inherited_model' as const,
        children: [...groupedInhDimNodes, ...inhGroupNodes, ...nestedNodes],
        metadata: {
          name: inherited.model,
          icon: inherited.icon,
          join: inherited.join,
          join_type: inherited.join_type,
          role: inherited.role,
        },
      };
    });

  const inheritedNodes = buildInheritedModelNodes(model.inheritedModels || []);

  const root: TreeNode = {
    id: `domain:${model.perspective}`,
    label: model.display_name || model.perspective,
    type: 'domain' as const,
    children: [...groupedOwnDimNodes, ...inheritedNodes, ...ownGroupNodes],
    metadata: {
      description: model.description,
      sample_questions: model.sample_questions,
      model: model.model,
      source_table: model.source_table,
    },
  };

  return [root];
}

/**
 * Returns a flat array of all DimensionDetail objects from a semantic model,
 * including root-level dimensions and all inherited model dimensions (recursive).
 */
export function getAllDimensions(model: SemanticModel): DimensionDetail[] {
  const dims: DimensionDetail[] = [...(model.dimensions || [])];

  const walkInherited = (inheritedModels?: InheritedModel[]) => {
    for (const inherited of inheritedModels || []) {
      dims.push(...(inherited.dimensions || []));
      if (inherited.inheritedModels) {
        walkInherited(inherited.inheritedModels);
      }
    }
  };

  walkInherited(model.inheritedModels);
  return dims;
}

export interface DimensionGroup {
  label: string;          // display name for the group header (e.g. "Consumer")
  dimensions: DimensionDetail[];
}

/**
 * Returns dimensions organized by source model, suitable for rendering
 * grouped dropdowns with section headers.
 *
 * Returns an array of groups:
 *   [{ label: "Delivery", dimensions: [...] }, { label: "Consumer", dimensions: [...] }, ...]
 *
 * The first group is always the root model's own dimensions.
 * Subsequent groups come from inherited models (recursive).
 */
export function getDimensionsByGroup(model: SemanticModel): DimensionGroup[] {
  const groups: DimensionGroup[] = [];

  // Root-level dimensions (own + core)
  const rootLabel = model.display_name || model.model || model.perspective;
  if (model.dimensions && model.dimensions.length > 0) {
    groups.push({ label: rootLabel, dimensions: model.dimensions });
  }

  // Inherited model dimensions (recursive)
  const walkInherited = (inheritedModels?: InheritedModel[]) => {
    for (const inherited of inheritedModels || []) {
      if (inherited.dimensions && inherited.dimensions.length > 0) {
        const label = inherited.display_name || toDisplayName(inherited.model);
        groups.push({ label, dimensions: inherited.dimensions });
      }
      if (inherited.inheritedModels) {
        walkInherited(inherited.inheritedModels);
      }
    }
  };

  walkInherited(model.inheritedModels);
  return groups;
}

export interface MetricGroupFlat {
  label: string;
  metrics: MetricDetail[];
}

/**
 * Returns metrics organized by source model / group, suitable for rendering
 * grouped dropdowns with section headers.
 *
 * Walks model.groups and model.inheritedModels recursively.
 */
export function getMetricsByGroup(model: SemanticModel): MetricGroupFlat[] {
  const groups: MetricGroupFlat[] = [];

  // Build a detail lookup from root-level metrics
  const metricDetailMap = new Map<string, MetricDetail>();
  (model.metrics || []).forEach((m) => metricDetailMap.set(m.name, m));

  // Root-level groups (own + core)
  for (const group of model.groups || []) {
    const details: MetricDetail[] = [];
    for (const ref of group.metrics || []) {
      const detail = metricDetailMap.get(ref.name);
      if (detail) details.push(detail);
    }
    if (details.length > 0) {
      groups.push({ label: group.display_name || group.name, metrics: details });
    }
  }

  // Inherited model metrics (recursive)
  const walkInherited = (inheritedModels?: InheritedModel[]) => {
    for (const inherited of inheritedModels || []) {
      if (inherited.metrics && inherited.metrics.length > 0) {
        const label = inherited.display_name || toDisplayName(inherited.model);
        groups.push({ label, metrics: inherited.metrics });
      }
      if (inherited.inheritedModels) {
        walkInherited(inherited.inheritedModels);
      }
    }
  };

  walkInherited(model.inheritedModels);
  return groups;
}

/** Detect if a metric formula is a simple single-aggregate: AGG(expr) */
const SIMPLE_AGG_PATTERN = /^(SUM|AVG|COUNT|MIN|MAX|MEDIAN|STDDEV|VARIANCE)\s*\(([^()]+)\)$/i;

export function isSimpleAggregateFormula(formula: string): boolean {
  return SIMPLE_AGG_PATTERN.test(formula.trim());
}

/** Get the default (first) date dimension from a semantic model (includes inherited) */
export function getDefaultDateDimension(model: SemanticModel): DimensionDetail | null {
  return getAllDimensions(model).find(d =>
    d.type === 'date' || d.type === 'datetime' || d.type === 'timestamp'
  ) || null;
}

/** Get all date dimensions from a semantic model (includes inherited) */
export function getDateDimensions(model: SemanticModel): DimensionDetail[] {
  return getAllDimensions(model).filter(d =>
    d.type === 'date' || d.type === 'datetime' || d.type === 'timestamp'
  );
}

/**
 * Basic SQL formatter that adds line breaks and indentation for readability.
 */
export function formatSQL(sql: string): string {
  if (!sql) return '';

  // Normalize whitespace
  let formatted = sql.replace(/\s+/g, ' ').trim();

  // Add line breaks before major keywords
  const keywords = [
    'SELECT',
    'FROM',
    'WHERE',
    'GROUP BY',
    'ORDER BY',
    'HAVING',
    'LIMIT',
    'OFFSET',
    'JOIN',
    'LEFT JOIN',
    'RIGHT JOIN',
    'INNER JOIN',
    'OUTER JOIN',
    'FULL OUTER JOIN',
    'LEFT OUTER JOIN',
    'RIGHT OUTER JOIN',
    'CROSS JOIN',
    'ON',
    'AND',
    'OR',
    'UNION',
    'UNION ALL',
    'INTERSECT',
    'EXCEPT',
    'WITH',
    'INSERT INTO',
    'VALUES',
    'UPDATE',
    'SET',
    'DELETE FROM',
  ];

  // Sort by length descending so longer keywords match first
  const sortedKeywords = [...keywords].sort((a, b) => b.length - a.length);

  sortedKeywords.forEach((keyword) => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    formatted = formatted.replace(regex, `\n${keyword}`);
  });

  // Indent sub-clauses
  const lines = formatted.split('\n').filter((line) => line.trim());
  const indentKeywords = ['AND', 'OR', 'ON'];
  const result = lines.map((line) => {
    const trimmed = line.trim();
    const upperTrimmed = trimmed.toUpperCase();
    if (indentKeywords.some((k) => upperTrimmed.startsWith(k))) {
      return '  ' + trimmed;
    }
    return trimmed;
  });

  return result.join('\n');
}

/**
 * Creates a debounced version of the given function.
 * The function will only be called after `delay` ms have elapsed
 * since the last invocation.
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  };
}

// ============================================================
// Date Range Presets
// ============================================================

export const DATE_RANGE_PRESETS: { key: DateRangePreset; label: string }[] = [
  { key: 'YESTERDAY',  label: 'Yesterday' },
  { key: 'LAST_7D',    label: 'Last 7 Days' },
  { key: 'WTD',        label: 'Week to Date' },
  { key: 'MTD',        label: 'Month to Date' },
  { key: 'QTD',        label: 'Quarter to Date' },
  { key: 'YTD',        label: 'Year to Date' },
  { key: 'LAST_MONTH',    label: 'Last Month' },
  { key: 'LAST_52_WEEKS', label: 'Last 52 Weeks' },
  { key: 'LAST_2_YEARS',  label: 'Last 2 Years' },
  { key: 'LAST_3_YEARS',  label: 'Last 3 Years' },
  { key: 'CUSTOM',        label: 'Custom' },
];

/** Resolve a preset key to { start, end } ISO date strings (client-side preview). */
export function resolvePresetDates(preset: DateRangePreset): { start: string; end: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const fmt = (d: Date) => d.toISOString().split('T')[0];

  switch (preset) {
    case 'YESTERDAY':
      return { start: fmt(yesterday), end: fmt(yesterday) };
    case 'LAST_7D': {
      const s = new Date(today);
      s.setDate(s.getDate() - 7);
      return { start: fmt(s), end: fmt(yesterday) };
    }
    case 'WTD': {
      const day = today.getDay(); // 0=Sun
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((day === 0 ? 7 : day) - 1));
      return { start: fmt(monday), end: fmt(yesterday) };
    }
    case 'MTD':
      return { start: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`, end: fmt(yesterday) };
    case 'QTD': {
      const quarter = Math.floor(today.getMonth() / 3);
      const qStartMonth = quarter * 3 + 1;
      return { start: `${today.getFullYear()}-${String(qStartMonth).padStart(2, '0')}-01`, end: fmt(yesterday) };
    }
    case 'YTD':
      return { start: `${today.getFullYear()}-01-01`, end: fmt(yesterday) };
    case 'LAST_MONTH': {
      const lm = today.getMonth() === 0 ? 11 : today.getMonth() - 1; // 0-indexed
      const lmy = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const lastDay = new Date(lmy, lm + 1, 0).getDate();
      return {
        start: `${lmy}-${String(lm + 1).padStart(2, '0')}-01`,
        end: `${lmy}-${String(lm + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
      };
    }
    case 'LAST_52_WEEKS': {
      const s = new Date(today);
      s.setDate(s.getDate() - 52 * 7);
      return { start: fmt(s), end: fmt(yesterday) };
    }
    case 'LAST_2_YEARS': {
      const s = new Date(today);
      s.setFullYear(s.getFullYear() - 2);
      return { start: fmt(s), end: fmt(yesterday) };
    }
    case 'LAST_3_YEARS': {
      const s = new Date(today);
      s.setFullYear(s.getFullYear() - 3);
      return { start: fmt(s), end: fmt(yesterday) };
    }
    default:
      return { start: '', end: '' };
  }
}

// ============================================================
// User identity helpers
// ============================================================

const USER_STORAGE_KEY = 'dx-current-user';

export function getCurrentUser(): string | null {
  return localStorage.getItem(USER_STORAGE_KEY);
}

export function setCurrentUser(username: string): void {
  localStorage.setItem(USER_STORAGE_KEY, username);
}

// ============================================================
// Relative timestamp
// ============================================================

export function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

// ============================================================
// Color helpers
// ============================================================

/** Generate a consistent color from a string (for avatars). */
export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    'var(--accent)', 'var(--ok)', 'var(--bad)', 'var(--agent)', 'var(--warn)',
    'var(--accent)', 'var(--agent)', 'var(--ink-2)', 'var(--bad)', 'var(--ok)',
  ];
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Generates a UUID-like unique identifier.
 * Uses crypto.randomUUID if available, otherwise falls back to a
 * manual implementation.
 */
export function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback: manual UUID v4 generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
